/**
 * NASDAQ Quantum — motor de presentación en cliente.
 *
 * TERCERA implementación independiente del motor (condición de Fase 2 de
 * Marc, roadmap cont. 355): junto con `backend/app/core/nasdaq_quantum.py`
 * (producción) y `backend/tests/_nasdaq_quantum_reference.py` (puerto de
 * referencia Python del test diferencial de Fase 1), este motor JS es el
 * que corre en el navegador sobre las velas YA CARGADAS del símbolo+TF
 * visible (Standard D.6: "el motor de PRESENTACIÓN... nunca un motor de
 * backend con su propio TF fijo"). Pasa su PROPIO test diferencial contra
 * el motor backend (`docs/research/nasdaq-quantum-paridad-js/`).
 *
 * Puerto fiel de `INDICADOR_NASDAQ_5M_Quantum_VIP.pine` (v6) — mismos 10
 * puntos no negociables que el motor Python (ver docstring de
 * `nasdaq_quantum.py`). Bucle vela a vela con ventanas deslizantes
 * (equivalente JS del puerto de referencia Python: sin dependencias, sin
 * pandas/numpy).
 *
 * Contrato de entrada: TODAS las velas recibidas deben estar CERRADAS
 * (`barstate.isconfirmed` del Pine) — quien llama a `detect()` es
 * responsable de excluir la vela en formación (Standard F.2: "todo motor
 * de presentación descarta la última vela ANTES de evaluar").
 */

export const defaultConfig = Object.freeze({
  sessA: '0500-0700',
  sessB: '0700-1030',
  sessC: '0700-1030',
  tz: 'Europe/Madrid',
  emaLen: 50,
  hugLen: 12,
  hugMin: 0.6,
  maxExt: 2.0,
  accLen: 36,
  maxCross: 4,
  erLen: 20,
  erMin: 0.20,
  rr: 0.4,
  maxTrades: 2,
  atrLen: 14,
})

function parseSession(spec) {
  const [startS, endS] = String(spec).split('-')
  const startMin = Number(startS.slice(0, 2)) * 60 + Number(startS.slice(2))
  const endMin = Number(endS.slice(0, 2)) * 60 + Number(endS.slice(2))
  return [startMin, endMin]
}

function inSession(minuteOfDay, startMin, endMin) {
  if (endMin > startMin) return minuteOfDay >= startMin && minuteOfDay < endMin
  return minuteOfDay >= startMin || minuteOfDay < endMin
}

// Intl.DateTimeFormat con `timeZone` usa la base de datos IANA real del
// motor JS (DST real, nunca offset fijo) — sin librerías externas.
function localMinuteOfDay(epochSec, tz) {
  const d = new Date(epochSec * 1000)
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hour12: false, hour: '2-digit', minute: '2-digit',
  }).formatToParts(d)
  const hh = Number(parts.find(p => p.type === 'hour')?.value ?? 0) % 24
  const mm = Number(parts.find(p => p.type === 'minute')?.value ?? 0)
  return hh * 60 + mm
}

function localDateOf(epochSec, tz) {
  const d = new Date(epochSec * 1000)
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(d)
}

function emptyResult() {
  return {
    ranges: [],
    signals: [],
    activeTrade: null,
    lastSignal: null,
    dashboard: {
      rangeState: 'waiting', rangeHigh: null, rangeLow: null,
      emaSeparated: null, distAtr: null, accumulation: null, nCross: null, er: null,
      tradesToday: 0, maxTrades: defaultConfig.maxTrades, activeSignal: null,
    },
  }
}

/**
 * @param {Array<{time:number, open:number, high:number, low:number, close:number}>} candles
 * @param {Object|null} config
 */
export function detect(candles, config) {
  const cfg = { ...defaultConfig, ...(config || {}) }
  if (!Array.isArray(candles) || candles.length === 0) return emptyResult()

  const n = candles.length
  const times = candles.map(c => Math.trunc(c.time))
  const opens = candles.map(c => Number(c.open))
  const highs = candles.map(c => Number(c.high))
  const lows = candles.map(c => Number(c.low))
  const closes = candles.map(c => Number(c.close))

  const emaLen = cfg.emaLen, atrLen = cfg.atrLen, hugLen = cfg.hugLen, hugMin = cfg.hugMin
  const maxExt = cfg.maxExt, accLen = cfg.accLen, maxCross = cfg.maxCross
  const erLen = cfg.erLen, erMin = cfg.erMin, rr = cfg.rr, maxTrades = cfg.maxTrades
  const tz = cfg.tz

  const sessA = parseSession(cfg.sessA)
  const sessB = parseSession(cfg.sessB)
  const sessC = parseSession(cfg.sessC)

  // ── EMA(50), sembrada con el primer close, alpha = 2/(len+1) ───────────
  const alpha = 2 / (emaLen + 1)
  const ema = new Array(n)
  ema[0] = closes[0]
  for (let i = 1; i < n; i++) ema[i] = alpha * closes[i] + (1 - alpha) * ema[i - 1]

  // ── ATR(14) Wilder/RMA, semilla = SMA de los primeros atrLen TR ────────
  const tr = new Array(n)
  tr[0] = highs[0] - lows[0]
  for (let i = 1; i < n; i++) {
    tr[i] = Math.max(highs[i] - lows[i], Math.abs(highs[i] - closes[i - 1]), Math.abs(lows[i] - closes[i - 1]))
  }
  const atr = new Array(n).fill(NaN)
  if (n >= atrLen) {
    let seed = 0
    for (let i = 0; i < atrLen; i++) seed += tr[i]
    seed /= atrLen
    for (let i = 0; i < atrLen; i++) atr[i] = seed
    for (let i = atrLen; i < n; i++) atr[i] = (atr[i - 1] * (atrLen - 1) + tr[i]) / atrLen
  }

  // ── Hora local (DST real) de cada vela ──────────────────────────────────
  const minuteOfDay = times.map(t => localMinuteOfDay(t, tz))

  // ── Estado por vela: ventanas deslizantes ───────────────────────────────
  const hugWindow = []
  const crossWindow = []
  const diffWindow = []

  let rHigh = null, rLow = null
  let ready = false, tradesToday = 0, brkDone = false, dayWin = false
  let active = false, isLong = false, entryPx = NaN, slPx = NaN, tpPx = NaN, entryBar = null

  const ranges = []
  const signals = []
  let currentRange = null
  let currentSignal = null

  let prevInA = false
  let prevInC = false
  let prevClose = null
  let prevEma = null

  for (let i = 0; i < n; i++) {
    const close = closes[i], high = highs[i], low = lows[i]

    const inA = inSession(minuteOfDay[i], sessA[0], sessA[1])
    const inB = inSession(minuteOfDay[i], sessB[0], sessB[1])
    const inC = inSession(minuteOfDay[i], sessC[0], sessC[1])
    const newA = inA && !prevInA
    const endedA = !inA && prevInA
    const endC = !inC && prevInC

    const a = atr[i]
    const distAtrI = Number.isFinite(a) && a > 0 ? (close - ema[i]) / a : 0.0
    hugWindow.push(Math.abs(distAtrI))
    if (hugWindow.length > hugLen) hugWindow.shift()
    const hugDistI = hugWindow.length === hugLen ? hugWindow.reduce((s, v) => s + v, 0) / hugLen : 0.0
    const huggedI = hugDistI < hugMin

    let crossed = false
    if (prevClose != null && prevEma != null) {
      crossed = (close > ema[i] && prevClose <= prevEma) || (close < ema[i] && prevClose >= prevEma)
    }
    crossWindow.push(crossed ? 1 : 0)
    if (crossWindow.length > accLen) crossWindow.shift()
    const nCrossI = crossWindow.length === accLen ? crossWindow.reduce((s, v) => s + v, 0) : 0.0

    const diff = prevClose != null ? Math.abs(close - prevClose) : null
    if (diff != null) {
      diffWindow.push(diff)
      if (diffWindow.length > erLen) diffWindow.shift()
    }
    let erI
    if (diffWindow.length === erLen && i >= erLen) {
      const noise = diffWindow.reduce((s, v) => s + v, 0)
      const num = Math.abs(close - closes[i - erLen])
      erI = noise > 0 ? num / noise : 1.0
    } else {
      erI = 1.0
    }
    const isAccI = nCrossI >= maxCross || erI < erMin
    const blockAccI = isAccI

    if (newA) {
      if (currentRange) {
        currentRange.endTime = i > 0 ? times[i - 1] : times[i]
        ranges.push(currentRange)
      }
      rHigh = high
      rLow = low
      ready = false
      tradesToday = 0
      brkDone = false
      dayWin = false
      if (active && currentSignal) {
        currentSignal.status = 'dropped_on_reset'
        currentSignal.closeReason = 'reset_newA'
        currentSignal.closeTime = times[i]
        signals.push(currentSignal)
        currentSignal = null
      }
      active = false
      currentRange = { date: localDateOf(times[i], tz), startTime: times[i], endTime: null, rangeHigh: null, rangeLow: null }
    }

    if (inA) {
      rHigh = rHigh == null ? high : Math.max(rHigh, high)
      rLow = rLow == null ? low : Math.min(rLow, low)
    }

    if (endedA && rHigh != null && rLow != null) {
      ready = true
      if (currentRange) {
        currentRange.rangeHigh = rHigh
        currentRange.rangeLow = rLow
      }
    }

    const canTrade = ready && tradesToday < maxTrades && !dayWin
    const rng = (rHigh != null && rLow != null) ? (rHigh - rLow) : 0.0

    const brkLong = canTrade && inB && !brkDone && !active
      && rHigh != null && close > rHigh
      && (close - rHigh) <= maxExt * a
      && close > ema[i] && !huggedI && !blockAccI

    const brkShort = canTrade && inB && !brkDone && !active
      && rLow != null && close < rLow
      && (rLow - close) <= maxExt * a
      && close < ema[i] && !huggedI && !blockAccI

    let sigDir = 0, sigSl = NaN, sigTp = NaN
    if (brkLong) {
      sigDir = 1
      sigSl = rLow
      sigTp = close + rng * rr
    } else if (brkShort) {
      sigDir = -1
      sigSl = rHigh
      sigTp = close - rng * rr
    }

    let closeNow = false, closePx = NaN, closeWhy = ''

    if (active && entryBar != null && i > entryBar) {
      const slHit = isLong ? low <= slPx : high >= slPx
      const tpHit = isLong ? high >= tpPx : low <= tpPx
      if (slHit) { closeNow = true; closePx = slPx; closeWhy = 'SL' }
      else if (tpHit) { closeNow = true; closePx = tpPx; closeWhy = 'TP' }
    }

    if (active && !closeNow && (endC || (sigDir !== 0 && sigDir !== (isLong ? 1 : -1)))) {
      closeNow = true
      closePx = close
      closeWhy = endC ? 'Cierre' : 'Giro'
    }

    if (closeNow) {
      if (closeWhy === 'TP') dayWin = true
      if (currentSignal) {
        currentSignal.status = 'closed'
        currentSignal.closeReason = closeWhy
        currentSignal.closeTime = times[i]
        currentSignal.closePx = closePx
        signals.push(currentSignal)
        currentSignal = null
      }
      active = false
    }

    if (sigDir !== 0) {
      active = true
      isLong = sigDir === 1
      entryPx = close
      slPx = sigSl
      tpPx = sigTp
      entryBar = i
      tradesToday += 1
      brkDone = true
      currentSignal = {
        day: localDateOf(times[i], tz),
        dir: isLong ? 'long' : 'short',
        entryTime: times[i],
        entryIdx: i,
        entry: entryPx,
        sl: slPx,
        tp: tpPx,
        rangeHigh: rHigh,
        rangeLow: rLow,
        status: 'active',
        closeReason: null,
        closeTime: null,
        closePx: null,
      }
    }

    prevInA = inA
    prevInC = inC
    prevClose = close
    prevEma = ema[i]
  }

  if (currentRange) {
    currentRange.endTime = times[n - 1]
    ranges.push(currentRange)
  }
  if (currentSignal) signals.push(currentSignal)

  const activeTrade = currentSignal && currentSignal.status === 'active' ? currentSignal : null
  const lastSignal = signals.length > 0 ? signals[signals.length - 1] : null

  const lastA = n > 0 ? atr[n - 1] : NaN
  const lastDistAtr = n > 0 ? (Number.isFinite(lastA) && lastA > 0 ? (closes[n - 1] - ema[n - 1]) / lastA : 0.0) : null
  const lastHugged = hugWindow.length === hugLen
    ? (hugWindow.reduce((s, v) => s + v, 0) / hugLen) < hugMin
    : (n > 0 ? 0 < hugMin : null)
  const lastNCross = crossWindow.length === accLen ? crossWindow.reduce((s, v) => s + v, 0) : 0.0
  let lastEr = 1.0
  if (diffWindow.length === erLen && n - 1 >= erLen) {
    const noise = diffWindow.reduce((s, v) => s + v, 0)
    const num = Math.abs(closes[n - 1] - closes[n - 1 - erLen])
    lastEr = noise > 0 ? num / noise : 1.0
  }
  const lastIsAcc = n > 0 ? (lastNCross >= maxCross || lastEr < erMin) : null

  const dashboard = {
    rangeState: ready ? 'ready' : (rHigh != null ? 'forming' : 'waiting'),
    rangeHigh: rHigh != null ? rHigh : null,
    rangeLow: rLow != null ? rLow : null,
    emaSeparated: n > 0 ? !lastHugged : null,
    distAtr: lastDistAtr,
    accumulation: lastIsAcc,
    nCross: n > 0 ? lastNCross : null,
    er: n > 0 ? lastEr : null,
    tradesToday,
    maxTrades,
    activeSignal: activeTrade,
  }

  // Serie de EMA por vela -- SOLO para el render en cliente (línea EMA del
  // chart, desviación #3 permitida del WP: "visual PRQ en lugar de dibujos
  // Pine, misma información, otro render"). El motor backend (Python,
  // producción/señales) no necesita este campo -- no lo expone, y el test
  // diferencial JS<->backend compara únicamente `signals`, nunca esta serie.
  const emaSeries = times.map((t, i) => ({ time: t, value: ema[i] }))

  return { ranges, signals, activeTrade, lastSignal, dashboard, emaSeries }
}
